import { nameToApiIdentifier } from "../../ApiIdentified";
import { InvokeCodeEffectSpecID } from "../../database/behaviour/effects/GadgetEffectLiterals";
import { GadgetSystemDominionId } from "../../database/DominionLiterals";
import { RequiredValidationId } from "../../database/validations/keys";
import { FrameworkPackageName } from "../../FrameworkPackageName";
import { FieldType } from "../../type-system/FieldType";
import { actionContextTypeName } from "../../TypeScriptCodeSupport";
import {
  ShopifyGdprRequestKey,
  ShopifyGdprRequestPayloadKey,
  ShopifyGdprRequestTopicKey,
  ShopifyGdprRequestToShopKey,
  ShopifyRequiredValidationKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyModelMetadataSpecOptions } from "./ShopifyModelMetadata";

const modelName = "Shopify GDPR Request";

export const ShopifyGdprRequest = (_options?: ShopifyModelMetadataSpecOptions) => {
  return {
    name: modelName,
    key: ShopifyGdprRequestKey,
    fields: [
      {
        key: ShopifyGdprRequestToShopKey,
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
        dominionID: GadgetSystemDominionId,
        contributorConfiguration: {
          [GadgetSystemDominionId]: { isWritableInternally: true, isWritablePublicly: true, storeInColumn: false },
        },
        validations: {
          [ShopifyRequiredValidationKey]: {
            key: ShopifyRequiredValidationKey,
            specID: RequiredValidationId,
          },
        },
      },
      {
        key: ShopifyGdprRequestTopicKey,
        name: "Topic",
        fieldType: FieldType.Enum,
        configuration: {
          allowMultiple: false,
          options: [{ name: "customers/data_request" }, { name: "customers/redact" }, { name: "shop/redact" }],
        },
        dominionID: GadgetSystemDominionId,
        contributorConfiguration: {
          [GadgetSystemDominionId]: {
            isWritableInternally: true,
            isWritablePublicly: true,
            storeInColumn: false,
          },
        },
        validations: {
          [ShopifyRequiredValidationKey]: {
            key: ShopifyRequiredValidationKey,
            specID: RequiredValidationId,
          },
        },
      },
      {
        key: ShopifyGdprRequestPayloadKey,
        name: "Payload",
        fieldType: FieldType.JSON,
        dominionID: GadgetSystemDominionId,
        contributorConfiguration: {
          [GadgetSystemDominionId]: {
            isWritableInternally: true,
            isWritablePublicly: true,
            storeInColumn: false,
          },
        },
      },
    ],
    stateChart: {
      includeCRUD: true,
      crudOptions: {
        defaultSuccessEffects: {
          create: [
            { specID: InvokeCodeEffectSpecID, configuration: { values: { sourceFilePath: "shopifyGdprRequest/create/handleTopic.js" } } },
          ],
        },
      },
    },
    files: [
      {
        path: "shopifyGdprRequest/create/handleTopic.js",
        data: shopifyGdprRequestCreateEffectCodeTemplate(),
      },
    ],
    syncConfig: { type: "webhook_only" } as const,
  };
};

const shopifyGdprRequestCreateEffectCodeTemplate = () => {
  const contextName = actionContextTypeName({
    actionApiIdentifier: "create",
    modelApiIdentifier: nameToApiIdentifier(modelName),
    modelNamespace: [],
  });

  return `/**
 * Effect code for Create on ${modelName}
 * @param { import("${FrameworkPackageName}").${contextName} } ${contextName} context - Everything for running this effect, like the api client, current record, params, etc
 */
module.exports = async ({ api, record, params }) => {
  switch(record.topic) {
    case "customers/data_request":
      // This process is a manual one. You must provide the customer's data to the store owners directly.
      // See https://shopify.dev/apps/webhooks/configuration/mandatory-webhooks#customers-data_request for more information.
      break;
    case "customers/redact":
      // Any modifications that are initiated by Shopify and emitted via webhook will automatically be handled by your existing model actions.
      // The responsibility falls on you to redact any additional customer related data you may have in custom models.
      break;
    case "shop/redact":
      // This will be received 48 hours after a store owner uninstalls your app. Any modifications that are initiated by Shopify and emitted via webhook will automatically be handled by your existing model actions.
      // The responsibility falls on you to redact any additional shop related data you may have in custom models.
      // See https://shopify.dev/apps/webhooks/configuration/mandatory-webhooks#shop-redact for more information.
      break;
  }
};
`;
};
